import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Container,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Refresh,
  Search,
  LocalOffer,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import packageService from '../services/packageService';
import { format } from 'date-fns';
import AddPackageDialog from '../components/AddPackageDialog';
import EditPackageDialog from '../components/EditPackageDialog';
import ViewPackageDialog from '../components/ViewPackageDialog';
import { useAuth } from '../hooks/useAuth';
import { Package } from '../types';

type PageLimit = 10 | 25 | 50;

const Packages: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<PageLimit>(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewPackageId, setViewPackageId] = useState<string | null>(null);
  const [editPackageId, setEditPackageId] = useState<string | null>(null);
  const [deletePackage, setDeletePackage] = useState<Package | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const apiPage = page + 1;

  const { data: packagesData, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['packages', apiPage, rowsPerPage, search, statusFilter, user?.role, user?.spop_id],
    queryFn: () => {
      const filters: Record<string, string> = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;
      if (user?.role === 'operator' && user?.spop_id) {
        filters.spop_id = user.spop_id;
      }
      return packageService.getPackages(apiPage, rowsPerPage, filters);
    },
  });

  const packages: Package[] = packagesData?.data?.packages ?? [];
  const total = packagesData?.data?.pagination?.total ?? 0;

  const stats = useMemo(() => {
    const active = packages.filter((p) => p.status === 'active').length;
    const inactive = packages.filter((p) => p.status === 'inactive').length;
    return { active, inactive, onPage: packages.length };
  }, [packages]);

  const deletePackageMutation = useMutation({
    mutationFn: (id: string) => packageService.deletePackage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      setSnackbarMessage('Paket berhasil dihapus');
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setDeletePackage(null);
    },
    onError: (err: any) => {
      setSnackbarMessage(err.response?.data?.message || 'Gagal menghapus paket');
      setSnackbarOpen(true);
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      packageService.toggleStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      setSnackbarMessage('Status paket diperbarui');
      setSnackbarOpen(true);
    },
    onError: (err: any) => {
      setSnackbarMessage(err.response?.data?.message || 'Gagal mengubah status');
      setSnackbarOpen(true);
    },
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  };

  const formatCurrency = (amount: number | string | undefined) => {
    const num = Number(amount ?? 0);
    if (!Number.isFinite(num)) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(num);
  };

  const formatBandwidth = (pkg: Package) => {
    const up = pkg.bandwidth_upload;
    const down = pkg.bandwidth_download;
    if (up && down) return `↑${up} / ↓${down}`;
    if (down) return `↓${down}`;
    if (up) return `↑${up}`;
    return pkg.profile_name || '-';
  };

  const formatQuota = (bytes?: number | string | null) => {
    if (!bytes || Number(bytes) === 0) return 'Unlimited';
    const n = Number(bytes);
    const gb = n / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(1)} GB`;
    const mb = n / (1024 * 1024);
    return `${mb.toFixed(0)} MB`;
  };

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput.trim());
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleViewPackage = (pkg: Package) => {
    setViewPackageId(pkg.id);
    setViewDialogOpen(true);
  };

  const handleEditPackage = (pkg: Package) => {
    setEditPackageId(pkg.id);
    setEditDialogOpen(true);
  };

  const handleEditFromView = (pkg: Package) => {
    setViewDialogOpen(false);
    setViewPackageId(null);
    setEditPackageId(pkg.id);
    setEditDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deletePackage?.id) {
      deletePackageMutation.mutate(deletePackage.id);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Paket Internet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Kelola paket layanan, harga, dan profile PPP
          </Typography>
          {user?.role === 'operator' && user?.spop && (
            <Chip
              size="small"
              label={`SPOP: ${user.spop.name}`}
              sx={{ mt: 1 }}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => refetch()} disabled={isFetching}>
            Muat Ulang
          </Button>
          {isAdmin && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setAddDialogOpen(true)}>
              Tambah Paket
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LocalOffer color="primary" />
              <Box>
                <Typography variant="h5" fontWeight={700}>{total}</Typography>
                <Typography variant="body2" color="text.secondary">Total Paket</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="h5" fontWeight={700} color="success.main">{stats.active}</Typography>
            <Typography variant="body2" color="text.secondary">Aktif (halaman ini)</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="h5" fontWeight={700} color="text.secondary">{stats.inactive}</Typography>
            <Typography variant="body2" color="text.secondary">Nonaktif (halaman ini)</Typography>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              size="small"
              placeholder="Cari nama paket, profile..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">Semua</MenuItem>
                <MenuItem value="active">Aktif</MenuItem>
                <MenuItem value="inactive">Nonaktif</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleSearch}>
              Cari
            </Button>
            {(search || statusFilter) && (
              <Button
                variant="text"
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setStatusFilter('');
                  setPage(0);
                }}
              >
                Reset
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Gagal memuat paket: {error instanceof Error ? error.message : 'Terjadi kesalahan'}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nama Paket</TableCell>
                      <TableCell>Profile PPP</TableCell>
                      <TableCell>Bandwidth</TableCell>
                      <TableCell align="right">Harga</TableCell>
                      <TableCell>Masa Aktif</TableCell>
                      <TableCell>Kuota</TableCell>
                      <TableCell>SPOP</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Dibuat</TableCell>
                      <TableCell align="center">Aksi</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {packages.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {search || statusFilter ? 'Tidak ada paket yang cocok dengan filter' : 'Belum ada paket internet'}
                          </Typography>
                          {isAdmin && !search && !statusFilter && (
                            <Button sx={{ mt: 1 }} startIcon={<Add />} onClick={() => setAddDialogOpen(true)}>
                              Tambah Paket Pertama
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      packages.map((pkg) => (
                        <TableRow key={pkg.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{pkg.name}</Typography>
                            {pkg.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {pkg.description.length > 50 ? `${pkg.description.slice(0, 50)}…` : pkg.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                              {pkg.profile_name}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatBandwidth(pkg)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {formatCurrency(pkg.price)}
                            </Typography>
                          </TableCell>
                          <TableCell>{pkg.validity_days ?? 30} hari</TableCell>
                          <TableCell>{formatQuota(pkg.quota_bytes)}</TableCell>
                          <TableCell>
                            {pkg.spop ? (
                              <Chip size="small" label={pkg.spop.name} variant="outlined" />
                            ) : (
                              <Typography variant="caption" color="text.secondary">Semua SPOP</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {isAdmin ? (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Switch
                                  size="small"
                                  checked={pkg.status === 'active'}
                                  onChange={(e) =>
                                    toggleStatusMutation.mutate({
                                      id: pkg.id,
                                      status: e.target.checked ? 'active' : 'inactive',
                                    })
                                  }
                                  disabled={toggleStatusMutation.isPending}
                                />
                                <Typography variant="caption">
                                  {pkg.status === 'active' ? 'Aktif' : 'Nonaktif'}
                                </Typography>
                              </Stack>
                            ) : (
                              <Chip
                                label={pkg.status === 'active' ? 'Aktif' : 'Nonaktif'}
                                color={pkg.status === 'active' ? 'success' : 'default'}
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>{formatDate(pkg.created_at)}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Detail">
                              <IconButton size="small" onClick={() => handleViewPackage(pkg)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {isAdmin && (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => handleEditPackage(pkg)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Hapus">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => { setDeletePackage(pkg); setDeleteDialogOpen(true); }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value) as PageLimit);
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Baris per halaman:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} dari ${count}`}
              />
            </>
          )}
        </CardContent>
      </Card>

      <AddPackageDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={(message) => {
          setSnackbarMessage(message);
          setSnackbarOpen(true);
        }}
      />

      <EditPackageDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditPackageId(null);
        }}
        packageId={editPackageId}
        onSuccess={(message) => {
          setSnackbarMessage(message);
          setSnackbarOpen(true);
        }}
      />

      <ViewPackageDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setViewPackageId(null);
        }}
        packageId={viewPackageId}
        canEdit={isAdmin}
        onEdit={handleEditFromView}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Hapus Paket?</DialogTitle>
        <DialogContent>
          <Typography>
            Hapus paket <strong>{deletePackage?.name}</strong>? Tindakan ini tidak dapat dibatalkan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Batal</Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deletePackageMutation.isPending}
          >
            {deletePackageMutation.isPending ? 'Menghapus...' : 'Hapus'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default Packages;
